/**
 * useHostCapture — the studio's half of the app's capture lifecycle.
 *
 * Mirrors what the iPhone host does around a native widget
 * (`widget_runtime_page._openCapture` → `dispatchWidgetRuntimeCapture`): adopt
 * the widget, open the capture sheet, upload the picked photo, dispatch ONE
 * session bound to this widget instance, draw the in-widget pending
 * placeholder while it runs, and refresh the feed when the log lands.
 *
 * Every step is a REAL endpoint — the same ones the HTML widget runtime uses:
 *   POST /api/widgets/{id}/instantiate      adopt (idempotent for standard)
 *   GET  /api/upload/presign + PUT to GCS   the photo — the sheet's own picker,
 *                                           and both answers to `pick_media`
 *                                           (`uploadSample` / `uploadPickedFile`,
 *                                           for a widget that collects its own
 *                                           materials)
 *   POST /api/users/exec                    the session — from the sheet, or
 *                                           HEADLESSLY (`dispatchCapture`) when
 *                                           the page already holds the photos
 *   GET  /api/users/events (SSE)            widget_data_changed / _appended
 *   POST /api/widget-instances/{id}/data    explicit Studio demo action only
 *
 * Failed or slow dispatches never manufacture result data. Only the real agent
 * write settles a requested capture; the existing explicit Studio demo action
 * remains separate from this automatic lifecycle.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../services/api';
import { describeApiError as errText } from '../../../services/apiError';
import { widgetInstances } from '../../../services/widgetApi';
import { composeHiddenSendPrompt, stripHiddenTags } from '../../../utils/text';
import { sampleAddition } from '../mock';

/** A slow generation deserves progress information, never a fabricated result. */
const PROGRESS_REMINDER_MS = 30_000;

/**
 * The newest row id in an instance's feed, null for empty, undefined on error.
 *
 * This is how the hook answers "did anything actually land?" — an id, not a
 * clock, so it is immune to skew between the server's timestamps and the
 * browser's.
 */
async function newestRowId(instanceId) {
  if (!instanceId) return null;
  try {
    const res = await widgetInstances.data(instanceId, { limit: 1 });
    const rows = Array.isArray(res) ? res : (res?.entries || []);
    return rows[0]?.id != null ? String(rows[0].id) : null;
  } catch {
    return undefined;
  }
}

/** Blob → data URL, the shape `api.uploadDataUrl` takes. */
function readAsDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * The FILE PICKER, as the browser's own dialog.
 *
 * `pickMedia` is the one host op that ANSWERS, and on the phone the answer is
 * whatever the user chose in the photo picker. The web equivalent is a real
 * `<input type="file">` click — nothing else opens a picker. It is mounted in
 * the document (never inside the phone frame, which clips and scrolls) and
 * removed as soon as it answers, so at most one is ever live.
 *
 * A cancelled picker answers NOTHING, exactly like the phone's: the browser
 * fires `cancel`, and the well returns to empty rather than filling itself with
 * something the user did not choose.
 */
export function openFilePicker({ multiple = false, signal } = {}) {
  const doc = globalThis.document;
  if (!doc?.body) return Promise.resolve([]);
  const input = doc.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.multiple = multiple;
  input.setAttribute('data-testid', 'studio-media-picker');
  input.style.display = 'none';
  doc.body.appendChild(input);
  return new Promise((resolve) => {
    const answer = (files) => {
      signal?.removeEventListener('abort', cancel);
      input.remove();
      resolve(files);
    };
    const cancel = () => answer([]);
    if (signal?.aborted) { cancel(); return; }
    signal?.addEventListener('abort', cancel, { once: true });
    input.addEventListener('change', () => answer(Array.from(input.files || [])), { once: true });
    input.addEventListener('cancel', () => answer([]), { once: true });
    input.click();
  });
}

export function useHostCapture({ widgetId, widgetTitle = null, dataMode, pageId = null }) {
  const [instanceId, setInstanceId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  // A blueprint session button (a scene-studio template, a captureFab with a
  // prompt) opens the SAME sheet the '+' does, but SEEDED with what the app's
  // `_openCapture` would carry: the resolved prompt, any pre-attached photos,
  // the session to continue, quick-mode. Null = the plain '+' open.
  const [seed, setSeed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [pendingAdds, setPendingAdds] = useState([]);
  const [session, setSession] = useState(null); // { clientRef, id, thumb }
  const [reloadKey, setReloadKey] = useState(0);
  const landed = useRef(0);
  const landing = useRef(false);
  // The in-flight capture, as a REF: a dispatch can fail before React has
  // re-rendered with the session state, and the landing still needs the photo.
  // `baseline` is the feed's newest row id before this capture — what proves a
  // later event really is the agent logging back.
  const flight = useRef({ clientRef: null, id: null, thumb: null, baseline: null });

  /**
   * THE CAPTURE SHEET BELONGS TO THE PAGE THAT ASKED FOR IT.
   *
   * Opening it is not instant: it adopts the widget and reads the feed's
   * baseline first — two round-trips, imperceptible locally and a second or
   * more on a deployed environment. In that gap the preview is still LIVE and
   * the developer keeps tapping, so a capture asked for on `index` used to
   * slide up over whatever page they had walked to by the time it resolved:
   * tap the widget's own capture button, walk 模板卡 → 详情 → Try, and the sheet
   * covers the role form asking for a photo the upload page was already
   * collecting. The app cannot do this — its `_openCapture` pushes a route over
   * the page that fired it, and a page it has left cannot push anything.
   *
   * So every open is stamped with the page that asked (`asked`) and lands only
   * if the preview is still there, and any page change closes an open sheet.
   * `dispatchCapture` deliberately has NO such guard: a session that was really
   * requested must still start, exactly as on the phone, wherever the preview
   * has wandered to meanwhile.
   */
  const pageRef = useRef(pageId);
  useEffect(() => { pageRef.current = pageId; }, [pageId]);
  useEffect(() => { setSheetOpen(false); setSeed(null); }, [pageId]);

  // Resolve the instance up front so the capture sheet can carry it (the
  // widget_use_contract binds the session to THIS instance). Adoption itself
  // waits for the trigger (`ensureInstance`), exactly like the app. Runs in
  // BOTH data modes now: a session started from Mock-data mode is still a REAL
  // dispatch on the signed-in user's instance — the mode only chooses which
  // feed the preview DISPLAYS, never whether the trigger is real.
  useEffect(() => {
    if (!widgetId) { setInstanceId(null); return undefined; }
    let alive = true;
    (async () => {
      try {
        const list = await widgetInstances.list();
        const rows = Array.isArray(list) ? list : (list?.instances || []);
        const mine = rows.find((i) => i.widget_id === widgetId);
        if (alive) setInstanceId(mine?.instance_id || null);
      } catch { /* the pane already reports the data state */ }
    })();
    return () => { alive = false; };
  }, [dataMode, widgetId]);

  /** The capture is over: drop the placeholder and refetch the real feed. */
  const settle = useCallback(() => {
    flight.current = { clientRef: null, id: null, thumb: null, baseline: null };
    setPendingAdds([]);
    setSession(null);
    setBusy(false);
    setNotice(null);
    setReloadKey((k) => k + 1);
  }, []);

  /** Adopt the widget (idempotent for standard) — the app's `useWidget()`. */
  const ensureInstance = useCallback(async () => {
    if (instanceId) return instanceId;
    const inst = await widgetInstances.instantiate(widgetId);
    const id = inst?.instance_id || inst?.id || null;
    setInstanceId(id);
    setReloadKey((k) => k + 1); // adopted now — leave the first-run state
    return id;
  }, [instanceId, widgetId]);

  /**
   * Remember the feed's newest row BEFORE a capture starts, so "the agent
   * logged back" can be told apart from "some other event about this widget
   * arrived". Taken when the sheet opens — long before Send — so it is always
   * ready by the time an event can race it.
   */
  const snapshot = useCallback(async (id) => {
    flight.current.baseline = await newestRowId(id);
  }, []);

  /** '+': adopt if needed, then show the plain (unseeded) sheet. */
  const openSheet = useCallback(async () => {
    setError(null);
    setBusy(true);
    const asked = pageRef.current;
    try {
      setSeed(null);
      await snapshot(await ensureInstance());
      if (pageRef.current !== asked) return; // stale — the preview walked away
      setSheetOpen(true);
    } catch (e) {
      setError(`Could not adopt this widget: ${errText(e)}`);
    } finally {
      setBusy(false);
    }
  }, [ensureInstance, snapshot]);

  /**
   * A blueprint session button fired `open_capture` (or a delegated
   * `open_page` whose preflight carries the template's locked prompt): adopt if
   * needed, then open the sheet SEEDED — the same thing the app's `_openCapture`
   * does for a bundle bridge action. `images` (stored `media://` refs) makes it
   * a scripted headless send; `note` prefills the prompt; `sessionId` continues
   * a session; `quick` collapses the sheet to the photo tile.
   */
  const openSheetSeeded = useCallback(async (s = {}) => {
    setError(null);
    setBusy(true);
    const asked = pageRef.current;
    try {
      if (s.openSession === true && s.sourceOrigin?.active === false) return;
      await snapshot(await ensureInstance());
      if (pageRef.current !== asked) return; // stale — the preview walked away
      if (s.openSession === true && s.sourceOrigin?.active === false) return;
      setSeed({
        note: s.note || null,
        images: Array.isArray(s.images) && s.images.length ? s.images : null,
        sessionId: s.sessionId || null,
        quick: s.quick === true,
        ...(s.openSession === true ? { openSession: true, sourcePageId: asked, sourceOrigin: s.sourceOrigin } : null),
      });
      setSheetOpen(true);
    } catch (e) {
      setError(`Could not adopt this widget: ${errText(e)}`);
    } finally {
      setBusy(false);
    }
  }, [ensureInstance, snapshot]);

  /**
   * `open_main_session {prompt}` — a support link, a "make another" fallback.
   * The main chat is NOT widget-bound and takes no photo, so it does not go
   * through the sheet or the widget-row degradation: it dispatches the prompt
   * straight to a new session and the banner links to it. Marked `main` so the
   * SSE settle and the land-a-row timeout (both widget-instance machinery) skip
   * it.
   */
  const dispatchMain = useCallback(async (prompt) => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const res = await api.dispatchExec({ prompt: (prompt && String(prompt).trim()) || ' ' });
      const id = res?.sessionId || res?.session_id || null;
      setSession({ clientRef: null, id, thumb: null, title: 'Main session', main: true });
    } catch (e) {
      setNotice(`The main session could not start here (${errText(e)}). A deployed runner handles it in the app.`);
    } finally {
      setBusy(false);
    }
  }, []);

  const closeSheet = useCallback(() => { setSheetOpen(false); setSeed(null); }, []);

  /**
   * ── THE TWO ANSWERS TO `pick_media` ───────────────────────────────────
   *
   * `pick_media` is THE HOST OP THAT ANSWERS. On the phone it opens the photo
   * picker and resolves with what the user chose; a widget that collects its own
   * materials (scene-studio's `template_upload` role form) is DEAD without it —
   * the wells never fill, the submit never leaves "Add photos above", and the
   * whole 模板卡 → 详情 → Try → 素材准备 → Create scene lane stops at the fourth
   * step.
   *
   * The studio offers BOTH photos a developer might mean, and the pane asks
   * which (see its pick menu): the widget's own representative sample — the
   * fast path for walking a lane end to end — or a real file off this machine,
   * which is the only way to see the lane with a photo it was not built around.
   * Neither is a shortcut past the upload: both ride `api.uploadDataUrl`
   * (`/api/upload/presign` → PUT → the media handle), so what a well ends up
   * holding, and therefore what the capture the submit fires carries, is always
   * a genuinely uploaded photo and never a foreign URL.
   *
   * Both answer with a LIST, and `[]` is the honest answer for "no photo" — a
   * dismissed menu, a cancelled picker, a failed upload. The well stays empty
   * and the form's own clock releases it, exactly as on the phone.
   *
   * Neither ADOPTS the widget. Picking a photo is not starting a session —
   * adoption belongs to the capture the submit fires, exactly as in the app.
   */

  /** "Upload Mock Image": the widget's representative photo, really uploaded. */
  const uploadSample = useCallback(async () => {
    try {
      const src = sampleAddition(widgetId)?.payload;
      // A photo-logging widget carries its sample under image_url/photo_url; a
      // document/multi-page one (doc-digitizer) carries pages under images[].
      const url = src?.image_url || src?.photo_url
        || (Array.isArray(src?.images) ? src.images.find((u) => typeof u === 'string' && u) : null);
      if (!url) throw new Error(`no sample photo for “${widgetId}”`);
      const blob = await (await fetch(url)).blob();
      const handle = await api.uploadDataUrl(await readAsDataUrl(blob));
      return handle ? [handle] : [];
    } catch (e) {
      setError(`Could not upload the sample photo (${errText(e)}).`);
      return [];
    }
  }, [widgetId]);

  /**
   * "Upload My Image": the browser's own picker, then the same upload.
   *
   * `max` is the picker's limit, the way the app's is: one well takes one
   * photo, a gallery slot takes several.
   */
  const uploadPickedFile = useCallback(async (max = 1) => {
    const limit = Math.max(1, Math.trunc(Number(max) || 1));
    let files = [];
    try {
      files = await openFilePicker({ multiple: limit > 1 });
    } catch { return []; }
    if (!files.length) return [];
    try {
      const urls = [];
      for (const file of files.slice(0, limit)) {
        // Serial on purpose: the ORDER is part of the answer — the Nth photo
        // must stay the Nth, and a parallel upload race would shuffle them.
        urls.push(await api.uploadDataUrl(await readAsDataUrl(file)));
      }
      return urls.filter(Boolean);
    } catch (e) {
      setError(`Could not upload that photo (${errText(e)}).`);
      return [];
    }
  }, []);

  /**
   * The pre-existing explicit Studio demo action. Automatic lifecycle paths
   * must never call this to turn an input photo into a supposed agent result.
   */
  const land = useCallback(async () => {
    // Never leave a placeholder spinning with nothing to write to.
    if (!instanceId) {
      setError('No widget instance to write to — tap + again to adopt it.');
      setPendingAdds([]);
      return;
    }
    // Repeated explicit button clicks still produce at most one write at a time.
    if (landing.current) return;
    landing.current = true;
    setBusy(true);
    try {
      landed.current += 1;
      const row = sampleAddition(widgetId, landed.current, { imageUrl: flight.current.thumb });
      if (row) {
        await widgetInstances.appendData(instanceId, {
          kind: row.kind,
          payload: row.payload,
          // A real session id makes the item's session affordances work — the
          // same binding the agent's own write would carry. There is none when
          // the dispatch itself failed; the row still lands.
          source_session_id: flight.current.id || null,
        });
      }
      settle();
    } catch (e) {
      setError(`Could not land the row: ${errText(e)}`);
      setBusy(false);
    } finally {
      landing.current = false;
    }
  }, [instanceId, settle, widgetId]);

  // ── the dispatch lifecycle the capture sheet drives ───────────────────
  const dispatchStarted = useCallback((clientRef, title, thumb) => {
    setError(null);
    setNotice(null);
    // keep the baseline taken when the sheet opened — it is what tells a
    // real landing apart from any other event about this widget.
    flight.current = { ...flight.current, clientRef, id: null, thumb: thumb || null };
    // The client_ref IS the placeholder key the server echoes back, so the
    // in-widget skeleton and the banner resolve off the same id.
    setPendingAdds([{ client_ref: clientRef, started_at: Date.now(), title, ...(thumb ? { thumb } : {}) }]);
    setSession({ clientRef, id: null, thumb: thumb || null, title });
  }, []);

  const dispatchResolved = useCallback((clientRef, id) => {
    if (flight.current.clientRef === clientRef) flight.current = { ...flight.current, id };
    setPendingAdds((p) => p.map((a) => (a.client_ref === clientRef ? { ...a, session_id: id } : a)));
    setSession((s) => (s && s.clientRef === clientRef ? { ...s, id } : s));
  }, []);

  /**
   * The session could not start at all — 402 (no credits on this account), a
   * gateway/runner that is down, a network error. Retract only this request's
   * placeholder and retain source inputs so the user can retry honestly.
   */
  const dispatchFailed = useCallback((clientRef, e) => {
    setPendingAdds((items) => items.filter((item) => item.client_ref !== clientRef));
    setSession((current) => current?.clientRef === clientRef ? null : current);
    if (flight.current.clientRef !== clientRef) return;
    flight.current = { clientRef: null, id: null, thumb: null, baseline: null };
    setBusy(false);
    setNotice(null);
    setError(`The Session could not start (${errText(e)}). Please try again.`);
  }, []);

  /**
   * A CAPTURE THE WIDGET ALREADY HOLDS THE PHOTOS FOR — dispatched HEADLESSLY.
   *
   * `open_capture {quick: true, images: […]}` is a page saying "I have already
   * collected everything; start the session". The app obeys it literally: no
   * sheet, no picker, no Send — `_openCapture` goes straight to
   * `dispatchWidgetRuntimeCapture`. Routing it through the studio's capture
   * sheet instead put a photo tile in front of a user who had just filled two
   * wells, and in quick mode the sheet even pops the file picker on open — the
   * widget asking a second time for the photos it is already carrying.
   *
   * So this is the app's path, step for step: adopt, mark the baseline, draw
   * the pending placeholder under a client_ref, dispatch ONE session bound to
   * this instance, and hand the outcome to the SAME three callbacks the sheet
   * uses — so the banner, the SSE settle and failure feedback behave
   * identically whichever door the capture came through.
   *
   * The composed prompt matches the sheet's exactly (`composeHiddenSendPrompt`
   * around the visible text), so a template's hidden `<vi_prefix>` blocks reach
   * the model here too.
   */
  const dispatchCapture = useCallback(async ({ note = null, images = [], sessionId = null, signal } = {}) => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const id = await ensureInstance();
      signal?.throwIfAborted();
      await snapshot(id);
      // 延迟上传页退出后，即使收养/读取实例刚完成，也不得新建 Session。
      signal?.throwIfAborted();
      const now = new Date();
      const two = (n) => String(n).padStart(2, '0');
      const stamp = `${now.getFullYear()}-${two(now.getMonth() + 1)}-${two(now.getDate())} `
        + `${two(now.getHours())}:${two(now.getMinutes())}`;
      const title = `${(widgetTitle || '').trim() || 'Capture'} ${stamp}`;
      const urls = (Array.isArray(images) ? images : []).filter((v) => typeof v === 'string' && v);
      const clientRef = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      dispatchStarted(clientRef, title, urls[0] || null);
      try {
        const res = await api.dispatchExec({
          prompt: composeHiddenSendPrompt(note, stripHiddenTags(note || '')) || ' ',
          // Stored refs the widget already collected — the ref IS the input
          // handle the backend resolves, so there is nothing left to upload.
          mediaUrls: urls.length ? urls : undefined,
          sessionId: sessionId || undefined,
          title,
          context: {
            widget_id: widgetId,
            widget_use_contract: { widget_id: widgetId, widget_instance_id: id },
            widget_client_ref: clientRef,
          },
        });
        const resolvedSessionId = String(res?.sessionId || res?.session_id || '').trim();
        if (!resolvedSessionId) throw new Error('The server did not return a Session ID');
        dispatchResolved(clientRef, resolvedSessionId);
        // Studio 目标页只使用服务端确认过的 Session，避免靠临时 client_ref 猜测结果归属。
        return {
          clientRef,
          sessionId: resolvedSessionId,
          title,
          thumb: urls[0] || null,
        };
      } catch (e) {
        dispatchFailed(clientRef, e);
        return null;
      }
    } catch (e) {
      setError(`Could not adopt this widget: ${errText(e)}`);
      return null;
    } finally {
      setBusy(false);
    }
  }, [dispatchFailed, dispatchResolved, dispatchStarted, ensureInstance, snapshot, widgetId, widgetTitle]);

  // The agent logs back — same event the HTML runtime listens on.
  //
  // An event about this widget is NOT proof a row landed: the session/widget
  // binding and other instance mutations publish here too, and settling on one
  // of those cleared the banner AND cancelled the fallback timer, leaving the
  // capture silently lost (the "banner appeared, then nothing" report). So an
  // event only settles the capture when it echoes OUR client_ref, or when the
  // feed's newest row has actually moved past the pre-capture baseline.
  // Anything else is just a reason to refetch and keep waiting.
  useEffect(() => {
    if (!session || session.main || !instanceId || typeof EventSource === 'undefined') return undefined;
    let es;
    let alive = true;
    try {
      es = new EventSource('/api/users/events', { withCredentials: true });
    } catch { return undefined; }
    const onEvent = async (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }
      if (msg.widget_instance_id !== instanceId && msg.widget_id !== widgetId) return;
      if (msg.client_ref && msg.client_ref === flight.current.clientRef) { settle(); return; }
      // An explicit different request is evidence for that request only.
      if (msg.client_ref) { setReloadKey((k) => k + 1); return; }
      const latest = await newestRowId(instanceId);
      if (!alive) return;
      // Failed reads, an empty feed, or an unknown baseline cannot prove a new
      // agent write. Keep the pending request until real evidence arrives.
      if (latest != null && flight.current.baseline !== undefined && latest !== flight.current.baseline) settle();
      else setReloadKey((k) => k + 1);
    };
    es.addEventListener('widget_data_changed', onEvent);
    es.addEventListener('widget_user_data_appended', onEvent);
    return () => { alive = false; es.close(); };
  }, [session, instanceId, widgetId, settle]);

  // Generation may take minutes. Only real agent data proves completion.
  useEffect(() => {
    if (!session || session.main) return undefined;
    const t = setTimeout(() => {
      setNotice('The Session is still working. Open it to follow progress; your result will appear when it is ready.');
    }, PROGRESS_REMINDER_MS);
    return () => clearTimeout(t);
  }, [session]);

  return {
    instanceId, sheetOpen, openSheet, openSheetSeeded, dispatchMain, dispatchCapture, closeSheet,
    seed, uploadSample, uploadPickedFile,
    busy, error, notice, pendingAdds, session, reloadKey, land,
    dispatchStarted, dispatchResolved, dispatchFailed,
  };
}

export default useHostCapture;
